import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Truck, MapPin, Package, Zap, Target, Clock, Moon, Sunset, Navigation, RefreshCw, Info } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/StatusBadge";
import OrderMatchMap from "@/components/OrderMatchMap";
import DriverGpsOnboarding from "@/components/DriverGpsOnboarding";
import DriverOperationalBanner from "@/components/DriverOperationalBanner";
import DriverActiveOpsBlock from "@/components/DriverActiveOpsBlock";
import { computeMatchScore } from "@/lib/match_score";
import { formatCHF, formatKm, relativeTime, SIZE_LABEL, URGENCY_LABEL } from "@/lib/format";
import { formatAddress } from "@/lib/formatAddress";

const VEHICLE_LABELS = {
  moto: "Moto",
  scooter: "Scooter",
  auto: "Auto",
  auto_grande: "Auto grande / SUV",
  furgone: "Furgone",
};
const AUTO_REFRESH_MS = 30_000;

export default function DriverHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);
  const [matchAlertDismissed, setMatchAlertDismissed] = useState(false);
  const [driverPos, setDriverPos] = useState(null); // { lat, lng } | null
  const [posState, setPosState] = useState("idle"); // idle | requesting | granted | denied | unavailable
  const [lastRefresh, setLastRefresh] = useState(null);
  const [routesCount, setRoutesCount] = useState(null); // null = unknown, number = count
  const [routesByName, setRoutesByName] = useState({});  // map route_name → route doc (for OrderMatchMap)
  const driverPosRef = useRef(driverPos);
  driverPosRef.current = driverPos;

  const vehicleType = user?.driver_info?.vehicle_type || null;
  const verified = !!user?.driver_info?.is_verified;
  const [complianceState, setComplianceState] = useState(null);
  const complianceBlocked = complianceState?.blocked === true;

  // Try to get the driver's current position once on mount.
  const requestPosition = () => {
    if (!("geolocation" in navigator)) {
      setPosState("unavailable");
      return;
    }
    setPosState("requesting");
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const pos = { lat: p.coords.latitude, lng: p.coords.longitude };
        setDriverPos(pos);
        setPosState("granted");
        // Phase 44.31.9 — Persist server-side "last known" del driver.
        // Permette al marketplace step 2 (radius fallback) di vedere il driver
        // anche fuori da un ordine attivo. Fire-and-forget, no toast su error.
        api.post("/driver/location", {
          lat: pos.lat, lng: pos.lng,
          accuracy_m: p.coords.accuracy,
        }).catch(() => { /* silent */ });
      },
      () => setPosState("denied"),
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: 8000 }
    );
  };

  useEffect(() => {
    requestPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch route count once on mount (Phase 31 — Commuter Delivery Network hint banner)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/driver/routes");
        const list = Array.isArray(data) ? data : [];
        setRoutesCount(list.length);
        const map = {};
        list.forEach((r) => { if (r.route_name) map[r.route_name] = r; });
        setRoutesByName(map);
      } catch {
        setRoutesCount(0);
        setRoutesByName({});
      }
    })();
  }, []);

  // Phase 43.27 — Fetch silenzioso compliance status per gate "Accetta" (banner pieno vive nel Profilo)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/driver/compliance-status");
        setComplianceState(data);
      } catch {/* silent */}
    })();
  }, []);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const pos = driverPosRef.current;
      const params = pos ? { driver_lat: pos.lat, driver_lng: pos.lng } : {};
      const { data } = await api.get("/orders/available", { params });
      setOrders(data);
      setLastRefresh(new Date());
    } catch (e) {
      if (!silent) toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Initial load + reload when driver position is acquired/changed
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [driverPos?.lat, driverPos?.lng]);

  // Auto-refresh every 30s so newly-created orders appear without manual reload
  useEffect(() => {
    const id = setInterval(() => load(true), AUTO_REFRESH_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const accept = async (id) => {
    if (complianceBlocked) {
      toast.error("Documento scaduto: aggiorna patente o assicurazione per tornare operativo.");
      return;
    }
    setAccepting(id);
    try {
      await api.post(`/orders/${id}/accept`);
      toast.success("Ordine accettato! Vai al ritiro.");
      // Phase 32 Batch B — redirect a schermata ritiro del nuovo ordine attivo
      navigate(`/driver/orders/${id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setAccepting(null);
    }
  };

  const matchedOrders = useMemo(() => orders.filter((o) => o.matches_driver_route), [orders]);

  // Client-side sort & filter controls
  const [sortBy, setSortBy] = useState("smart"); // smart | distance | earning | route_match | recent
  const [matchedOnly, setMatchedOnly] = useState(false);
  const [minScore, setMinScore] = useState(0); // Phase 31.4 — soglia compatibilità (0 = tutti, 80, 90)

  const visibleOrders = useMemo(() => {
    let arr = [...orders];
    if (matchedOnly) arr = arr.filter((o) => o.matches_driver_route);
    // Phase 31.4 — filtro per compatibilità minima (attivo solo quando matchedOnly === true)
    if (matchedOnly && minScore > 0) {
      arr = arr.filter((o) => {
        const r = o.matched_route_name ? routesByName[o.matched_route_name] : null;
        if (!r) return true; // no route info → considera compatibile senza score (per spec)
        const info = computeMatchScore({
          pickupLat: o.pickup_lat, pickupLng: o.pickup_lng,
          deliveryLat: o.delivery_lat, deliveryLng: o.delivery_lng,
          routeStartLat: r.start_lat, routeStartLng: r.start_lng,
          routeEndLat: r.end_lat, routeEndLng: r.end_lng,
          maxDetourKm: typeof r.max_detour_km === "number" ? r.max_detour_km : 5,
        });
        if (!info) return true; // dati insufficienti → compatibile senza score
        return info.score >= minScore;
      });
    }
    const earningOf = (o) => Number(o.driver_payout ?? o.driver_earning ?? 0);
    const distanceOf = (o) => (typeof o.distance_from_driver_km === "number" ? o.distance_from_driver_km : Number.POSITIVE_INFINITY);
    const orderDistOf = (o) => Number(o.distance_km ?? 0);
    const tsOf = (o) => {
      try { return new Date(o.created_at).getTime(); } catch { return 0; }
    };
    if (sortBy === "distance") {
      arr.sort((a, b) => distanceOf(a) - distanceOf(b));
    } else if (sortBy === "earning") {
      arr.sort((a, b) => earningOf(b) - earningOf(a));
    } else if (sortBy === "route_match") {
      arr.sort((a, b) => (b.matches_driver_route ? 1 : 0) - (a.matches_driver_route ? 1 : 0) || tsOf(b) - tsOf(a));
    } else if (sortBy === "recent") {
      arr.sort((a, b) => tsOf(b) - tsOf(a));
    } else if (sortBy === "shortest_trip") {
      arr.sort((a, b) => orderDistOf(a) - orderDistOf(b));
    }
    // "smart" → trust backend default ordering (route-match → proximity → recent)
    return arr;
  // Phase 31.4 — filtered list
  }, [orders, sortBy, matchedOnly, minScore, routesByName]);

  // Phase 31.5 — counter compatibilità: quanti ordini matched superano 80% / 90%
  const compatCounts = useMemo(() => {
    const matched = orders.filter((o) => o.matches_driver_route);
    let n80 = 0;
    let n90 = 0;
    matched.forEach((o) => {
      const r = o.matched_route_name ? routesByName[o.matched_route_name] : null;
      if (!r) { n80 += 1; n90 += 1; return; } // dati insufficienti → considera compatibile
      const info = computeMatchScore({
        pickupLat: o.pickup_lat, pickupLng: o.pickup_lng,
        deliveryLat: o.delivery_lat, deliveryLng: o.delivery_lng,
        routeStartLat: r.start_lat, routeStartLng: r.start_lng,
        routeEndLat: r.end_lat, routeEndLng: r.end_lng,
        maxDetourKm: typeof r.max_detour_km === "number" ? r.max_detour_km : 5,
      });
      if (!info) { n80 += 1; n90 += 1; return; }
      if (info.score >= 80) n80 += 1;
      if (info.score >= 90) n90 += 1;
    });
    return { n80, n90, total: matched.length };
  }, [orders, routesByName]);

  // In-app notification toast for new matched orders (one-shot per load)
  useEffect(() => {
    if (!loading && matchedOrders.length > 0 && !matchAlertDismissed) {
      toast.success(
        matchedOrders.length === 1
          ? `🎯 1 ordine sul tuo tragitto abituale!`
          : `🎯 ${matchedOrders.length} ordini sul tuo tragitto abituale!`,
        { id: "matched-routes" }
      );
      setMatchAlertDismissed(true);
    }
  }, [loading, matchedOrders.length, matchAlertDismissed]);

  return (
    <div className="space-y-5" data-testid="driver-home">
      {/* Phase 43.51 — Blocco operativo live SEMPRE in cima (mostrato solo se ci
          sono ordini attivi). Trasforma la home in console operativa quando serve. */}
      <DriverActiveOpsBlock />

      {/* Phase 43.27 — Banner operativo compatto (sostituisce checklist+compliance pieni, ora in Profilo) */}
      <DriverOperationalBanner />

      <div className="flex items-end justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <p className="overline">Driver</p>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter">Ordini disponibili</h1>
            <button
              onClick={() => load()}
              data-testid="driver-refresh-btn"
              title="Aggiorna lista"
              aria-label="Aggiorna lista"
              className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-dapnow-line bg-white text-dapnow-muted hover:text-dapnow-ink hover:border-dapnow-ink transition"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
          {matchedOrders.length > 0 && (
            <p className="text-xs text-emerald-700 font-semibold mt-1" data-testid="matched-summary">
              {matchedOrders.length} {matchedOrders.length === 1 ? "ordine compatibile" : "ordini compatibili"} con i tuoi tragitti
            </p>
          )}
        </div>
      </div>

      {/* Phase 44.31.5 — CTA hero "Sulla mia strada" (tragitto occasionale).
          Design "blu pieno": card prominente subito sotto l'header per dare
          priorità al CTA principale del modello commuter. */}
      <Link
        to="/driver/route-search"
        data-testid="route-search-cta"
        className="block rounded-2xl bg-dapnow-blue p-5 sm:p-6 text-white shadow-[0_8px_24px_rgba(0,82,255,0.18)] hover:shadow-[0_10px_28px_rgba(0,82,255,0.24)] hover:-translate-y-0.5 transition group overflow-hidden relative"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/70">Driver · Feature</p>
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1.5 leading-tight">
              Sulla mia strada
            </h2>
            <p className="text-sm text-white/85 mt-2 leading-relaxed max-w-md">
              Stai andando da qualche parte? Trova consegne compatibili lungo il tuo percorso.
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-white text-dapnow-blue text-sm font-bold group-hover:bg-white/95 transition shrink-0">
              Trova consegne <span aria-hidden>→</span>
            </span>
          </div>
          <div className="rounded-full bg-white/15 backdrop-blur-sm p-3 shrink-0 ring-1 ring-white/30">
            <Navigation className="size-6" />
          </div>
        </div>
      </Link>

      {/* Vehicle filter banner — explains why list might be empty */}
      {vehicleType ? (
        <div className="rounded-2xl border border-dapnow-line bg-white px-4 py-2.5 text-xs text-dapnow-ink flex items-center gap-2" data-testid="driver-vehicle-banner">
          <Info className="h-3.5 w-3.5 text-dapnow-blue" />
          <span>
            Stai vedendo solo ordini compatibili con il tuo veicolo:{" "}
            <strong className="font-semibold">{VEHICLE_LABELS[vehicleType] || vehicleType}</strong>.
            Gli ordini più grandi sono visibili a driver con veicolo idoneo.
          </span>
        </div>
      ) : (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2" data-testid="driver-no-vehicle-banner">
          <Truck className="h-4 w-4 mt-0.5 shrink-0" />
          <div>
            <strong className="font-semibold">Nessun veicolo configurato.</strong>{" "}
            Aggiungi il tuo veicolo per ricevere ordini compatibili.{" "}
            <Link to="/driver/vehicle" className="underline font-semibold">Configura ora →</Link>
          </div>
        </div>
      )}

      {!verified && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <strong className="font-semibold">Account in verifica.</strong> Puoi vedere gli ordini, ma per accettarli serve la verifica documenti dall'admin.
        </div>
      )}

      {/* Phase 43.12 — Compliance KYC stato (fetch silenzioso per gate "Accetta") */}
      {/* Il banner pieno vive nel Profilo: qui solo lo stato per disabilitare i bottoni Accetta */}

      {/* Preserve optional route records without promoting Shared as an internal-fleet service. */}
      {routesCount === 0 && (
        <div
          className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 text-sm text-emerald-900 flex items-start gap-3"
          data-testid="commuter-onboarding-banner"
        >
          <Target className="h-5 w-5 mt-0.5 shrink-0 text-emerald-700" />
          <div className="flex-1">
            <strong className="font-semibold">Tragitti Shared · Servizio futuro.</strong>{" "}
            Shared è previsto per i driver privati e non è ancora operativo. I tragitti registrati restano
            consultabili; questa impostazione facoltativa non attiva l'accesso al servizio e non è richiesta per Standard con la flotta interna.
          </div>
          <Link to="/driver/routes" className="btn-primary shrink-0" data-testid="commuter-onboarding-cta">
            Aggiungi tragitto
          </Link>
        </div>
      )}

      {posState === "granted" && driverPos && null}

      {/* Phase 43.6 — Onboarding GPS non-bloccante (mostrato quando posizione non
          è ancora concessa o è stata negata). Si auto-nasconde se l'utente clicca
          "Lo farò più tardi" (per 7 giorni). */}
      <DriverGpsOnboarding posState={posState} onEnable={requestPosition} />

      {posState === "denied" && (
        <div className="rounded-2xl border border-dapnow-line bg-dapnow-bg/50 px-4 py-2.5 text-xs text-dapnow-muted flex items-center gap-2 flex-wrap" data-testid="driver-position-denied">
          <Navigation className="h-3.5 w-3.5" />
          <span className="flex-1 min-w-[180px]">
            Posizione non disponibile — ordinamento per match e data. Abilita la geolocalizzazione per vedere gli ordini più vicini.
          </span>
          <button
            type="button"
            onClick={requestPosition}
            data-testid="driver-position-retry"
            className="rounded-full bg-dapnow-ink text-white font-semibold px-3 py-1 hover:opacity-90 transition text-[11px]"
          >
            Riprova
          </button>
        </div>
      )}

      {/* Filters / sort bar — Phase 44.31.6 riordino logico */}
      <div className="card-soft p-3 space-y-3" data-testid="driver-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="overline mr-1">Ordina per</span>
          {[
            { id: "smart",         label: "Consigliati" },
            { id: "route_match",   label: "Sul mio tragitto" },
            { id: "distance",      label: "Più vicini" },
            { id: "earning",       label: "Compenso più alto" },
            { id: "shortest_trip", label: "Tragitto corto" },
            { id: "recent",        label: "Più recenti" },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setSortBy(opt.id)}
              data-testid={`driver-sort-${opt.id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold tracking-tight transition ${
                sortBy === opt.id
                  ? "bg-dapnow-blue text-white shadow"
                  : "bg-dapnow-bg text-dapnow-ink hover:bg-dapnow-blue-soft"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-dapnow-line/60">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none text-xs">
            <input
              type="checkbox"
              checked={matchedOnly}
              onChange={(e) => {
                const v = e.target.checked;
                setMatchedOnly(v);
                if (!v) setMinScore(0);
              }}
              data-testid="driver-filter-matched-only"
              className="h-4 w-4 rounded border-dapnow-line accent-emerald-600"
            />
            <span className="font-semibold">Solo sul mio tragitto</span>
          </label>
          {matchedOnly && (
            <div
              className="inline-flex items-center gap-1.5 ml-1 rounded-full bg-emerald-50 border border-emerald-200 px-1.5 py-1"
              data-testid="driver-min-score-filter"
              role="group"
              aria-label="Compatibilità minima"
            >
              <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 px-1.5 hidden sm:inline">
                Compatibilità min.
              </span>
              {[
                { v: 0, label: "Tutti" },
                { v: 80, label: "≥80%" },
                { v: 90, label: "≥90%" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setMinScore(opt.v)}
                  data-testid={`min-score-${opt.v}`}
                  className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold transition ${
                    minScore === opt.v
                      ? "bg-emerald-600 text-white shadow-sm"
                      : "bg-white text-emerald-800 hover:bg-emerald-100"
                  }`}
                  aria-pressed={minScore === opt.v}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          {matchedOnly && compatCounts.total > 0 && (
            <span
              className="text-[10px] sm:text-[11px] font-medium text-emerald-700 basis-full sm:basis-auto -mt-1 sm:mt-0"
              data-testid="driver-compat-counter"
              aria-label="Conteggio compatibilità"
            >
              <span className="hidden sm:inline">
                {compatCounts.n80} {compatCounts.n80 === 1 ? "ordine" : "ordini"} ≥80% · {compatCounts.n90} {compatCounts.n90 === 1 ? "ordine" : "ordini"} ≥90%
              </span>
              <span className="sm:hidden">
                ≥80%: {compatCounts.n80} · ≥90%: {compatCounts.n90}
              </span>
            </span>
          )}
          <span className="ml-auto text-[10px] text-dapnow-muted">
            {visibleOrders.length} / {orders.length} ordini
          </span>
        </div>
      </div>

      {loading ? (
        <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>
      ) : visibleOrders.length === 0 ? (
        <div className="card-soft p-10 text-center" data-testid="driver-empty">
          <Truck className="h-6 w-6 mx-auto text-dapnow-blue" />
          <p className="mt-3 font-display text-lg font-semibold tracking-tight">
            {matchedOnly && minScore > 0
              ? "Nessun ordine supera questa soglia"
              : matchedOnly
                ? "Nessun ordine sul tuo tragitto al momento"
                : `Nessun ordine disponibile${vehicleType ? ` per ${VEHICLE_LABELS[vehicleType] || vehicleType}` : ""}`}
          </p>
          <p className="text-sm text-dapnow-muted mt-1 max-w-md mx-auto">
            {matchedOnly && minScore > 0
              ? "Nessun ordine supera questa soglia di compatibilità. Abbassa il filtro o visualizza tutti gli ordini sul tuo percorso."
              : matchedOnly
                ? "Disattiva il filtro 'Solo sul mio tragitto' per vedere tutti gli ordini compatibili."
                : vehicleType
                  ? "Possono esistere ordini per altri tipi di veicolo. La lista si aggiorna in automatico ogni 30 secondi, oppure premi Aggiorna."
                  : "Aggiungi il tuo veicolo per iniziare a ricevere ordini compatibili."}
          </p>
          {matchedOnly && minScore > 0 && (
            <button
              type="button"
              onClick={() => setMinScore(0)}
              data-testid="driver-empty-reset-score"
              className="btn-ghost text-xs mt-4 inline-flex"
            >
              Mostra tutti
            </button>
          )}
          {!matchedOnly && vehicleType && (
            <Link to="/driver/vehicle" className="btn-ghost text-xs mt-4 inline-flex" data-testid="driver-empty-vehicle-link">
              Cambia veicolo
            </Link>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {visibleOrders.map((o) => (
            <div
              key={o.id}
              className={`card-soft p-5 relative min-w-0 ${o.matches_driver_route ? "!border-2 !border-emerald-500/70 bg-emerald-50/30" : ""}`}
              data-testid={`driver-available-${o.id}`}
            >
              {o.matches_driver_route && (
                <span
                  data-testid={`route-match-badge-${o.id}`}
                  className="absolute -top-2 left-4 inline-flex items-center gap-1 rounded-full bg-emerald-600 text-white px-2.5 py-1 text-[11px] font-semibold shadow"
                >
                  <Target className="h-3 w-3" /> 🎯 Sul tuo percorso{o.matched_route_name ? ` · ${o.matched_route_name}` : ""}
                </span>
              )}
              <div className="flex justify-between items-start">
                <div>
                  <p className="overline">#{o.id.slice(0, 8).toUpperCase()}</p>
                  <p className="text-xs text-dapnow-muted">{relativeTime(o.created_at)}</p>
                </div>
                <StatusBadge status={o.status} />
              </div>

              <div className="mt-4 space-y-2.5 text-sm">
                <div className="flex items-start gap-2.5">
                  <span className="mt-1 h-2 w-2 rounded-full bg-dapnow-blue ring-4 ring-dapnow-blue-soft shrink-0" />
                  <p className="truncate">{formatAddress(o.pickup_address)}</p>
                </div>
                <div className="ml-[3px] h-3 w-px bg-dapnow-line" />
                <div className="flex items-start gap-2.5">
                  <span className="mt-1 h-2 w-2 rounded-full bg-dapnow-red ring-4 ring-red-100 shrink-0" />
                  <p className="truncate">{formatAddress(o.delivery_address)}</p>
                </div>
              </div>

              {/* Phase 31.2 — Mini-mappa di compatibilità mostrata SOLO se l'ordine matcha un tragitto driver */}
              {o.matches_driver_route && o.matched_route_name && routesByName[o.matched_route_name] && (
                <div className="mt-4">
                  <OrderMatchMap
                    pickupLat={o.pickup_lat}
                    pickupLng={o.pickup_lng}
                    deliveryLat={o.delivery_lat}
                    deliveryLng={o.delivery_lng}
                    routeStartLat={routesByName[o.matched_route_name].start_lat}
                    routeStartLng={routesByName[o.matched_route_name].start_lng}
                    routeEndLat={routesByName[o.matched_route_name].end_lat}
                    routeEndLng={routesByName[o.matched_route_name].end_lng}
                    maxDetourKm={typeof routesByName[o.matched_route_name].max_detour_km === "number" ? routesByName[o.matched_route_name].max_detour_km : 5}
                    matchedRouteName={o.matched_route_name}
                    usualTimeWindow={routesByName[o.matched_route_name].usual_time_window}
                  />
                </div>
              )}

              <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-dapnow-muted">
                <span className="inline-flex items-center gap-1"><Package className="h-3.5 w-3.5" /> {SIZE_LABEL[o.size]?.split(" ")[0]}</span>
                <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {formatKm(o.distance_km)}</span>
                {typeof o.distance_from_driver_km === "number" && (
                  <span data-testid={`driver-distance-${o.id}`} className="inline-flex items-center gap-1 rounded-full bg-dapnow-blue-soft text-dapnow-blue px-2 py-0.5 font-semibold">
                    <Navigation className="h-3.5 w-3.5" /> {formatKm(o.distance_from_driver_km)} da te
                  </span>
                )}
                <span className="inline-flex items-center gap-1 rounded-full bg-dapnow-bg px-2 py-0.5"><Zap className="h-3.5 w-3.5" /> {URGENCY_LABEL[o.urgency]}</span>
                {o.time_slot && o.time_slot !== "day" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 font-semibold">
                    {o.time_slot === "night" ? <Moon className="h-3.5 w-3.5" /> : <Sunset className="h-3.5 w-3.5" />}
                    {o.time_slot_name || (o.time_slot === "night" ? "Notte" : "Sera")}
                  </span>
                )}
                {o.is_weekend && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 text-purple-800 px-2 py-0.5 font-semibold">
                    <Clock className="h-3.5 w-3.5" /> Weekend
                  </span>
                )}
              </div>

              <div className="mt-5 flex items-end justify-between gap-2 flex-wrap">
                <div className="min-w-0">
                  <p className="overline text-[10px]">Valore contabile</p>
                  <p className="font-display text-2xl font-bold tracking-tight">{formatCHF(o.driver_earning)}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to={`/driver/orders/${o.id}?preview=1`}
                    data-testid={`driver-preview-${o.id}`}
                    className="inline-flex items-center justify-center h-11 px-3 rounded-xl border border-dapnow-line bg-white text-dapnow-ink text-sm font-semibold hover:border-dapnow-ink transition"
                  >
                    Visualizza
                  </Link>
                  <button
                    onClick={() => accept(o.id)}
                    disabled={!verified || complianceBlocked || accepting === o.id}
                    title={complianceBlocked ? "Documento scaduto: aggiorna patente o assicurazione per tornare operativo." : undefined}
                    data-testid={`driver-accept-${o.id}`}
                    className="inline-flex items-center justify-center h-11 px-4 rounded-xl bg-dapnow-blue text-white text-sm font-bold hover:bg-dapnow-blue-hover transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {accepting === o.id ? "Accetto…" : (complianceBlocked ? "Bloccato" : "Accetta")}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
